import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { SearchAutocomplete } from '@/components/search/SearchAutocomplete';
import { AlgoliaSearchAutocomplete } from '@/components/search/AlgoliaSearchAutocomplete';
import { getAlgoliaDisplaySuggestions } from '@/lib/suggestions/algoliaDisplaySuggestions';
import { getAlgoliaSequenceSuggestions } from '@/lib/suggestions/algoliaSequenceSuggestions';
import { Suggestion } from '@/lib/suggestions/types';

interface SearchTabsProps {
  className?: string;
  activeTab: 'displays' | 'sequences';
  setActiveTab: (tab: 'displays' | 'sequences') => void;
}

export const SearchTabs: React.FC<SearchTabsProps> = ({ className, activeTab, setActiveTab }) => {
  // Use single shared state for search text that persists between tabs
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleDisplaySearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/displays?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSequenceSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/sequences/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleDisplaySuggestionSelect = (suggestion: Suggestion) => {
    if (suggestion.type === 'holiday') {
      // Navigate to displays with holiday filter pre-selected
      navigate(`/displays?holiday=${encodeURIComponent(suggestion.value)}`);
    } else if (suggestion.type === 'display' && suggestion.displayId) {
      // Navigate directly to display detail page
      navigate(`/display/${suggestion.displayId}`);
    } else if (suggestion.type === 'display') {
      // Fallback: Navigate to displays search with display name query if no ID
      setSearchQuery(suggestion.value);
      navigate(`/displays?q=${encodeURIComponent(suggestion.value)}`);
    }
  };

  const handleSequenceSuggestionSelect = (suggestion: Suggestion) => {
    if (suggestion.type === 'song' && suggestion.sequenceId) {
      // Navigate directly to sequence detail page
      navigate(`/sequence/${suggestion.sequenceId}`);
    } else if (suggestion.type === 'song') {
      // Fallback: Navigate to sequences search with song title query if no ID
      setSearchQuery(suggestion.value);
      navigate(`/sequences/search?q=${encodeURIComponent(suggestion.value)}`);
    } else if (suggestion.type === 'genre') {
      // Navigate to sequences search with genre filter pre-selected
      navigate(`/sequences/search?genre=${encodeURIComponent(suggestion.value)}`);
    } else if (suggestion.type === 'software') {
      // Navigate to software-specific discovery landing page
      navigate(`/sequences/${encodeURIComponent(suggestion.value)}`);
    } else if (suggestion.type === 'holiday') {
      // Navigate to sequences search with holiday filter pre-selected
      navigate(`/sequences/search?holiday=${encodeURIComponent(suggestion.value)}`);
    }
  };

  return (
    <Tabs
      defaultValue="displays"
      value={activeTab}
      onValueChange={(value) => setActiveTab(value as 'displays' | 'sequences')}
      className={cn("w-full max-w-lg", className)}
    >
      <TabsList className="grid w-full grid-cols-2 bg-white/10 backdrop-blur-xl border border-white/20">
        <TabsTrigger
          value="displays"
          className="data-[state=active]:bg-green-500/90 data-[state=active]:text-white flex items-center gap-2"
        >
          <MapPin size={16} />
          <span className="hidden sm:inline">Search</span> Displays
        </TabsTrigger>
        <TabsTrigger
          value="sequences"
          className="data-[state=active]:bg-blue-500/90 data-[state=active]:text-white flex items-center gap-2"
        >
          <Sparkles size={16} />
          <span className="hidden sm:inline">Search</span> Sequences
        </TabsTrigger>
      </TabsList>

      <TabsContent value="displays" className="mt-4">
        <div className="relative">
          <form onSubmit={handleDisplaySearch} className="bg-white/10 backdrop-blur-xl border border-white/20 p-1 rounded-full flex items-center">
          <AlgoliaSearchAutocomplete
            placeholder="Search for holiday displays near you..."
            icon={<MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-green-400 z-10" size={18} />}
            value={searchQuery}
            onChange={setSearchQuery}
            onSubmit={handleDisplaySearch}
            getSuggestionsAsync={getAlgoliaDisplaySuggestions}
            onSelectSuggestion={handleDisplaySuggestionSelect}
            inputClassName="border-0 bg-transparent pl-10 text-white placeholder:text-white/60 focus-visible:ring-0 focus-visible:ring-offset-0"
            dropdownClassName="left-0 right-0"
            indexName="displays"
          />
          <Button
            type="submit"
            className="rounded-full ml-1 px-4 bg-green-500 hover:bg-green-600"
          >
            <Search size={18} className="mr-2" /> Search
          </Button>
        </form>
        </div>
      </TabsContent>

      <TabsContent value="sequences" className="mt-4">
        <div className="relative">
          <form onSubmit={handleSequenceSearch} className="bg-white/10 backdrop-blur-xl border border-white/20 p-1 rounded-full flex items-center">
            <AlgoliaSearchAutocomplete
              placeholder="Search for xLights and Light-O-Rama sequences"
              icon={<Sparkles className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 z-10" size={18} />}
              value={searchQuery}
              onChange={setSearchQuery}
              onSubmit={handleSequenceSearch}
              getSuggestionsAsync={getAlgoliaSequenceSuggestions}
              onSelectSuggestion={handleSequenceSuggestionSelect}
              inputClassName="border-0 bg-transparent pl-10 text-white placeholder:text-white/60 focus-visible:ring-0 focus-visible:ring-offset-0"
              dropdownClassName="left-0 right-0"
              indexName="sequences"
            />
            <Button
              type="submit"
              className="rounded-full ml-1 px-4 bg-blue-500 hover:bg-blue-600"
            >
              <Search size={18} className="mr-2" /> Search
            </Button>
          </form>
        </div>
      </TabsContent>
    </Tabs>
  );
};
